import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, X as XIcon, Users, Network, Briefcase, Calculator,
  Headset, TrendingUp, UserCog, Building2,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";
import Seo, { faqJsonLd } from "@/components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  PROGRESSION, COMPARISON, SERVICES, EXAMPLE_TEAMS, MANAGED_JOURNEY, FAQS, TARGET_MARKETS,
} from "@/lib/content";

const teamIcons = { briefcase: Briefcase, calc: Calculator, headset: Headset, trend: TrendingUp };
const managedFaqs = FAQS.filter((f) =>
  ["start with one employee", "difference between EOR", "dedicated exclusively", "chooses the employees",
   "manages daily work", "team leader", "entire department", "existing systems", "office later",
   "quickly can a team", "managed-team pricing", "team member leaves", "own Indian company"]
    .some((k) => f.q.toLowerCase().includes(k.toLowerCase())));

const pod = SERVICES.find((s) => s.slug === "team-pod");
const func = SERVICES.find((s) => s.slug === "offshore-function");

const ManagedTeams = () => {
  return (
    <>
      <Seo
        title="Managed Teams in India for UK Businesses | Bright EOR"
        description="Build a dedicated India-based team without opening an Indian company. Bright EOR recruits, employs, equips and supports individual hires, team pods and complete offshore functions."
        path="/managed-teams"
        jsonLd={faqJsonLd(managedFaqs)}
      />

      <PageHeader
        eyebrow="Managed Teams"
        title={`Build a dedicated India team around your business.`}
        subtitle="From a two-person support pod to a complete offshore function, Bright EOR recruits, employs, equips and supports professionals who work exclusively for your company."
        ctaLabel="Plan My India Team"
        ctaTo="/contact"
      />

      {/* Start with one and scale */}
      <section className="py-20 lg:py-28" data-testid="mt-progression">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Start with one. Scale when it works.</p>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
              One hire today. A full function when you&apos;re ready.
            </h2>
            <p className="mt-6 text-lg text-slateink leading-relaxed">
              Single-hire EOR remains at the core of what we do. Managed teams simply extend it — you can begin with one dedicated employee and add people as the model proves itself.
            </p>
          </div>
          <div className="relative mt-12">
            <div className="hidden lg:block absolute top-8 left-[8%] right-[8%] h-px border-t-2 border-dashed border-aqua/30" aria-hidden />
            <div className="grid gap-6 lg:grid-cols-4">
              {PROGRESSION.map((p) => (
                <div key={p.stage} className="relative" data-testid={`mt-stage-${p.stage.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className="w-16 h-16 rounded-2xl bg-navy text-aqua font-display text-lg font-semibold flex items-center justify-center shadow-card-soft">
                    {p.stage.replace("Stage ", "")}
                  </div>
                  <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-card-soft hover:border-aqua/40 transition-all">
                    <h3 className="font-display text-lg font-semibold text-navy tracking-tight">{p.title}</h3>
                    <p className="mt-2 text-[14px] text-slateink leading-relaxed">{p.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Outsourcing vs Bright EOR */}
      <Comparison />

      {/* Managed Team Pod */}
      <ServiceBlock service={pod} testid="mt-pod" bg="" icon={Users} />

      {/* Managed Offshore Function + options */}
      <section className="bg-navy-50 py-20 lg:py-28" data-testid="mt-function">
        <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="w-14 h-14 rounded-2xl bg-aqua/15 text-navy flex items-center justify-center"><Network className="h-6 w-6" /></div>
              <p className="mt-6 text-xs uppercase tracking-[0.22em] text-aqua font-semibold">{func.level}</p>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-semibold text-navy leading-[1.08] tracking-tight">{func.headline}</h2>
              <p className="mt-4 text-lg text-slateink leading-relaxed">{func.tagline}</p>
              <p className="mt-3 text-[15px] text-slatemuted leading-relaxed">{func.body}</p>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-card-soft">
                <p className="text-xs uppercase tracking-[0.22em] text-slatemuted font-semibold">Included</p>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {func.includes.map((inc) => (
                    <li key={inc} className="flex items-start gap-2.5 text-[15px] text-slateink">
                      <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-aqua/15"><Check className="h-3 w-3 text-aqua" strokeWidth={3} /></span>
                      {inc}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Client-led vs fully managed */}
          <div className="mt-8 grid gap-5 md:grid-cols-2" data-testid="mt-options">
            {func.options.map((o, i) => (
              <div key={o.name} className={`rounded-3xl p-8 border ${i === 1 ? "bg-navy text-white border-navy" : "bg-white border-slate-200"}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${i === 1 ? "bg-aqua text-navy" : "bg-aqua/15 text-navy"}`}>
                  {i === 1 ? <UserCog className="h-5 w-5" /> : <Users className="h-5 w-5" />}
                </div>
                <h3 className={`font-display mt-5 text-xl font-semibold tracking-tight ${i === 1 ? "text-white" : "text-navy"}`}>{o.name}</h3>
                <p className={`mt-2 text-[14.5px] leading-relaxed ${i === 1 ? "text-white/80" : "text-slateink"}`}>{o.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Example teams */}
      <section className="py-20 lg:py-28" data-testid="mt-example-teams">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Example teams</p>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">Complete teams, built around your business.</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {EXAMPLE_TEAMS.map((t, i) => {
              const Icon = teamIcons[t.icon] || Briefcase;
              return (
                <div key={t.name} className="rounded-3xl bg-white border border-slate-200 p-7 hover:border-aqua/40 hover:-translate-y-1 hover:shadow-card-hover transition-all" data-testid={`mt-team-${i}`}>
                  <div className="w-11 h-11 rounded-2xl bg-aqua/15 text-navy flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                  <h3 className="font-display mt-5 text-lg font-semibold text-navy tracking-tight leading-snug">{t.name}</h3>
                  <ul className="mt-4 space-y-1.5">
                    {t.roles.map((r) => (
                      <li key={r} className="flex items-start gap-2 text-[13.5px] text-slateink"><span className="mt-1.5 w-1 h-1 rounded-full bg-aqua shrink-0" />{r}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team-building process */}
      <section className="bg-navy text-white py-20 lg:py-28 relative overflow-hidden" data-testid="mt-journey">
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-aqua/10 blur-3xl" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Team-building process</p>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">How we build your team.</h2>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {MANAGED_JOURNEY.map((s) => (
              <div key={s.step} className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6" data-testid={`mt-step-${s.step}`}>
                <span className="font-display text-2xl font-semibold text-aqua">{s.step}</span>
                <h3 className="font-display mt-3 text-[16px] font-semibold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-[13.5px] text-white/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target industries */}
      <section className="py-20 lg:py-28" data-testid="mt-industries">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Target industries</p>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">Repeatable functions we build often.</h2>
          </div>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {TARGET_MARKETS.map((m) => (
              <div key={m.industry} className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-[14.5px] font-medium text-navy flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-aqua" />{m.industry}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-4 lg:py-8" data-testid="mt-faq">
        <div className="mx-auto max-w-4xl px-6 md:px-10 lg:px-12 pb-20">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Managed teams — FAQ</p>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">Common questions.</h2>
          </div>
          <div className="mt-12">
            <Accordion type="single" collapsible className="w-full">
              {managedFaqs.map((item, i) => (
                <AccordionItem key={i} value={`mt-item-${i}`} className="border-slate-200" data-testid={`mt-faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-[17px] font-medium text-navy hover:no-underline hover:text-aqua py-5">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-[15px] text-slateink leading-relaxed pb-5">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
};

const Comparison = () => (
  <section className="bg-navy-50 py-20 lg:py-28" data-testid="mt-comparison">
    <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.22em] text-aqua font-semibold">Not outsourcing</p>
        <h2 className="font-display mt-4 text-4xl sm:text-5xl font-semibold text-navy tracking-tight leading-[1.05]">
          The difference between outsourcing and <span className="text-aqua">Bright EOR.</span>
        </h2>
      </div>
      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl bg-white border border-slate-200 p-8 lg:p-10">
          <p className="text-xs uppercase tracking-[0.22em] text-slatemuted font-semibold">Traditional outsourcing</p>
          <ul className="mt-6 space-y-3">
            {COMPARISON.traditional.map((t) => (
              <li key={t} className="flex items-start gap-3 text-[15px] text-slateink">
                <span className="mt-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-navy-50 border border-slate-300"><XIcon className="h-3 w-3 text-slatemuted" /></span>{t}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-3xl bg-navy text-white p-8 lg:p-10 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-aqua/20 blur-3xl" aria-hidden />
          <p className="relative text-xs uppercase tracking-[0.22em] text-aqua font-semibold">The Bright EOR way</p>
          <ul className="relative mt-6 space-y-3">
            {COMPARISON.brighteor.map((t) => (
              <li key={t} className="flex items-start gap-3 text-[15px] text-white/90">
                <span className="mt-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-aqua text-navy"><Check className="h-3 w-3" strokeWidth={3} /></span>{t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const ServiceBlock = ({ service, testid, icon: Icon }) => (
  <section className="py-20 lg:py-28" data-testid={testid}>
    <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="w-14 h-14 rounded-2xl bg-aqua/15 text-navy flex items-center justify-center"><Icon className="h-6 w-6" /></div>
          <p className="mt-6 text-xs uppercase tracking-[0.22em] text-aqua font-semibold">{service.level}</p>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-semibold text-navy leading-[1.08] tracking-tight">{service.headline}</h2>
          <p className="mt-4 text-lg text-slateink leading-relaxed">{service.tagline}</p>
          <p className="mt-3 text-[15px] text-slatemuted leading-relaxed">{service.body}</p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-[15px] font-medium text-white hover:bg-aqua hover:text-navy transition-all">
            Plan My India Team <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="lg:col-span-7">
          <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-card-soft">
            <p className="text-xs uppercase tracking-[0.22em] text-slatemuted font-semibold">Included</p>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2">
              {service.includes.map((inc) => (
                <li key={inc} className="flex items-start gap-2.5 text-[15px] text-slateink">
                  <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-aqua/15"><Check className="h-3 w-3 text-aqua" strokeWidth={3} /></span>{inc}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default ManagedTeams;
